import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Drawer, Typography, MenuItem, IconButton, InputAdornment, Divider
} from '@mui/material';
import { 
  X, Save, User, Mail, Lock, DollarSign, Percent, Shield, ToggleLeft, Users as UsersIcon, Briefcase
} from 'lucide-react';
import api from '../../services/api';

export default function EmployeeForm({ open, onClose, onSave, selected }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role_id: '',
    hourly_rate: '',
    commission_percent: '',
    status: 'active',
    group_id: '' // New field for the group
  });
  
  const [groups, setGroups] = useState([]);

  const roleOptions = [
    { id: 1, name: 'Employee' },
    { id: 2, name: 'Manager' },
    { id: 3, name: 'Admin' },
    { id: 4, name: 'Supervisor' },
    { id: 5, name: 'Material' }
  ];

  // Fetches groups when the form is opened
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await api.get('/groups');
        setGroups(response.data);
      } catch (error) {
        console.error("Error fetching groups:", error);
        // Consider adding user feedback here
      }
    };

    if (open) {
      fetchGroups();
    }
  }, [open]);

  // Updates the form when an employee is selected or the form is reopened
  useEffect(() => {
    if (selected) {
      setForm({
        ...selected,
        password: '', // Clears the password for security
        hourly_rate: selected.hourly_rate || '',
        commission_percent: selected.commission_percent || '',
        group_id: selected.group_id || '' // Ensures the value is controlled
      });
    } else {
      // Resets the form for a new employee
      setForm({
        name: '',
        email: '',
        password: '',
        role_id: '',
        hourly_rate: '',
        commission_percent: '',
        status: 'active',
        group_id: ''
      });
    }
  }, [selected, open]); // Added 'open' to reset when reopening

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      // Converts group_id to null if it's an empty string, otherwise to an integer
      const groupIdToSend = form.group_id === '' ? null : parseInt(form.group_id, 10);

      const payload = {
        ...form,
        role_id: parseInt(form.role_id, 10),
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        commission_percent: parseFloat(form.commission_percent) || 0,
        group_id: groupIdToSend,
      };

      // Removes the password from the payload if it's empty (to avoid unnecessary updates)
      if (!payload.password) {
        delete payload.password;
      }

      if (!selected && !form.password) {
        alert('Password is required for new employees.');
        return;
      }

      if (selected) {
        await api.put(`/employees/${selected.id}`, payload);
      } else {
        await api.post('/employees', payload);
      }

      onSave(); // Updates the employee list
      onClose(); // Closes the form
    } catch (err) {
      const errorMessage = err.response?.data?.msg || err.message;
      alert(`Error saving employee: ${errorMessage}`);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 300, p: 3, backgroundColor: '#fff', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UsersIcon size={24} color="#1976d2" />
            <Typography variant="h6" fontWeight="bold">
              {selected ? 'Edit Employee' : 'New Employee'}
            </Typography>
          </Box>
          <IconButton onClick={onClose}><X size={20} /></IconButton>
        </Box>

        {/* Form Content */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflowY: 'auto', pr: 1 }}>
          <TextField name="name" label="Full Name" value={form.name} onChange={handleChange} fullWidth required InputProps={{ startAdornment: <InputAdornment position="start"><User size={18} /></InputAdornment> }} />
          <TextField name="email" label="Email Address" value={form.email} onChange={handleChange} fullWidth required type="email" InputProps={{ startAdornment: <InputAdornment position="start"><Mail size={18} /></InputAdornment> }} />
          <TextField name="password" label={selected ? "New Password (optional)" : "Password"} type="password" value={form.password} onChange={handleChange} fullWidth required={!selected} InputProps={{ startAdornment: <InputAdornment position="start"><Lock size={18} /></InputAdornment> }} />
          
          <Divider />

          <TextField name="hourly_rate" label="Hourly Rate" value={form.hourly_rate} onChange={handleChange} fullWidth type="number" InputProps={{ startAdornment: <InputAdornment position="start"><DollarSign size={18} /></InputAdornment> }} />
          <TextField name="commission_percent" label="Commission Percentage" value={form.commission_percent} onChange={handleChange} fullWidth type="number" InputProps={{ startAdornment: <InputAdornment position="start"><Percent size={18} /></InputAdornment> }} />

          <Divider />

          {/* Access, Status & Group */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>Access & Status</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Role Select */}
              <TextField name="role_id" label="Role" select value={form.role_id} onChange={handleChange} fullWidth required InputProps={{ startAdornment: <InputAdornment position="start"><Shield size={18} /></InputAdornment> }}>
                {roleOptions.map((role) => (
                  <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                ))}
              </TextField>

              {/* Status Select */}
              <TextField name="status" label="Status" select value={form.status} onChange={handleChange} fullWidth required InputProps={{ startAdornment: <InputAdornment position="start"><ToggleLeft size={18} /></InputAdornment> }}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>

              {/* Group Select */}
              <TextField
                name="group_id"
                label="Group"
                select
                value={form.group_id}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Briefcase size={18} color="#666" />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="">
                  <em>No Group</em>
                </MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
          <Button variant="contained" onClick={handleSubmit} startIcon={<Save size={18} />} fullWidth>
            {selected ? 'Update Employee' : 'Create Employee'}
          </Button>
          <Button onClick={onClose} startIcon={<X size={18} />} fullWidth sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}